/**
 * Council Adapter
 *
 * Maps frontend input → API payload, normalizes responses → UI schema.
 * Handles retries (max 2), timeout, error classification.
 * Strictly READ-ONLY — no trade execution, no portfolio impact.
 */

import { analyzeCouncil, CouncilApiError, pollCouncilDeliberation } from '../lib/apiClient';
import type { CouncilDeliberationPollResult } from '../lib/apiClient';
import type {
  CouncilDeliberation,
  CouncilAnalysisRequest,
  CouncilGatewayResponse,
  CouncilGatewayData,
  NormalizedCouncilResult,
  CouncilInsight,
  CouncilRisk,
  CouncilErrorType,
} from '../types/council';

// ═══════════════════════════════════════════════════════════════════════
//  CONFIG
// ═══════════════════════════════════════════════════════════════════════

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

let debugMode = false;

export function setCouncilDebugMode(enabled: boolean): void {
  debugMode = enabled;
}

// ═══════════════════════════════════════════════════════════════════════
//  NORMALIZE RESPONSE
// ═══════════════════════════════════════════════════════════════════════

export function normalizeCouncilResponse(response: CouncilGatewayResponse): NormalizedCouncilResult {
  // 2026-05-14: Detect NEW backend contract first
  //   { status, code, message, request_id, council_members[],
  //     synthesis:{summary, risk_notes, decision}, data_quality, ... }
  // and fall back to the legacy { data: {...}, meta: {...} } envelope.
  const r: any = response ?? {};
  const isNewContract =
    typeof r.status === 'string' &&
    (Array.isArray(r.council_members) || r.synthesis || r.data_quality);

  if (isNewContract) {
    const members: any[] = Array.isArray(r.council_members) ? r.council_members : [];
    const ready = members.filter(
      (m: any) => m && (m.status === 'ready' || m.status === 'limited'),
    );

    // Insights: ALWAYS one entry per Council member (all four), with
    // status-aware content so the UI never silently hides a member that
    // errored or is not configured. Ready/limited members render their
    // real summary; error members render an explicit error line; missing
    // members render a "not configured" line.
    const insights: CouncilInsight[] = members.map((m: any) => {
      const name = m?.name || m?.id || 'Provider';
      const status = (m?.status || '').toString();
      if (status === 'ready' || status === 'limited') {
        const sm = (typeof m?.summary === 'string' && m.summary.trim()) ? m.summary.trim() : null;
        return { agent: name, content: sm || `${name} contributed to the council decision.` };
      }
      if (status === 'error') {
        const detail = (typeof m?.summary === 'string' && m.summary.trim()) ? m.summary.trim() : '';
        return {
          agent: `${name} (error)`,
          content: detail || `${name} encountered an error and did not participate in this analysis.`,
        };
      }
      // unavailable / missing
      return {
        agent: `${name} (unavailable)`,
        content: `${name} is not configured on this account or did not participate in this Council run.`,
      };
    });

    // Confidence: max of participating members' confidence; fallback to 0.
    let confidence = 0;
    for (const m of ready) {
      const c = m?.confidence;
      if (typeof c === 'number' && isFinite(c) && c > confidence) confidence = c;
    }

    // Risks: synthesis.risk_notes mapped to {label, severity}.
    const riskNotes = Array.isArray(r?.synthesis?.risk_notes) ? r.synthesis.risk_notes : [];
    const risks: CouncilRisk[] = riskNotes
      .filter((s: any) => typeof s === 'string' && s.trim().length > 0)
      .map((s: string) => ({ label: s.trim(), severity: 'medium' as const }));

    // Provider-coverage note: always reflect the X-of-4 reality.
    const totalMembers = members.length || 4;
    const readyCount = ready.length;
    const missingNames = members
      .filter((m: any) => m && m.status !== 'ready' && m.status !== 'limited')
      .map((m: any) => m?.name || m?.id || 'provider');
    if (readyCount < totalMembers && missingNames.length > 0) {
      const label = `Partial Council coverage (${readyCount} of ${totalMembers} providers reporting; missing: ${missingNames.join(', ')}).`;
      if (!risks.find((rk) => rk.label.toLowerCase().includes('council coverage') || rk.label.toLowerCase().includes('partial'))) {
        risks.unshift({ label, severity: 'medium' as const });
      }
    } else if (r.status === 'LIMITED' && !risks.find((rk) => rk.label.toLowerCase().includes('single-provider'))) {
      risks.unshift({ label: 'Single-provider analysis (limited coverage).', severity: 'medium' });
    }

    // Decision -> recommendations list.
    // 2026-05-15: prefer the backend's NEW `synthesis.recommendation` string —
    // it carries the live-context-aware text (e.g. "Adversyn live context IS
    // available; the missing pieces are specific: economic calendar, …")
    // instead of the legacy hardcoded "gather live data and re-run" line.
    // Only fall back to the pretty-print map if the backend didn't ship one.
    const decision = typeof r?.synthesis?.decision === 'string' ? r.synthesis.decision : '';
    const backendRecommendation =
      typeof r?.synthesis?.recommendation === 'string' && r.synthesis.recommendation.trim()
        ? r.synthesis.recommendation.trim()
        : '';
    const recommendations: string[] = [];
    if (backendRecommendation) {
      recommendations.push(backendRecommendation);
    } else if (decision) {
      const pretty: Record<string, string> = {
        paper_watchlist: 'Add to paper-trading watchlist.',
        no_trade: 'No trade recommended at this time.',
        needs_more_data:
          'Council has run, but the orchestrator did not return a specific recommendation this cycle. ' +
          'Re-run after the next Market News fetch (every 15 min) before treating any signal as actionable.',
        unavailable: 'Council analysis unavailable.',
      };
      recommendations.push(pretty[decision] || `Decision: ${decision}`);
    }

    // 2026-05-15: surface live-context metadata in a single status bullet so the
    // UI can show users *what* context the Council actually saw.
    const dq: any = r?.data_quality || {};
    if (dq && typeof dq === 'object') {
      const contextInjected = dq.context_injected === true;
      const newsCount = typeof dq.news_item_count === 'number' ? dq.news_item_count : null;
      const symbolCount = typeof dq.symbol_news_count === 'number' ? dq.symbol_news_count : null;
      const sources: string[] = Array.isArray(dq.sources) ? dq.sources : [];
      const missingArr: string[] = Array.isArray(dq.missing) ? dq.missing : [];
      // [data-health-frontend-v1]
      const assetClass = String((r as any)?.asset_class || dq?.asset_class || '').toLowerCase();
      const isCrypto = assetClass === 'crypto' || /btc|eth|usdt|sol/i.test(String(r?.symbol || ''));
      const optionsStatus = String(dq?.options_status || '').toUpperCase();
      const optionsProviderNotConfigured = optionsStatus === 'OPTIONS_PROVIDER_NOT_CONFIGURED';
      const namedMissing = missingArr
        .map((s) => String(s).split(':')[0])
        .filter((s) => {
          // Crypto must NEVER show equity options_flow missing.
          if (s === 'options_flow' && isCrypto) return false;
          // For equities where the options provider simply is not configured,
          // we surface a separate explanatory bullet — don't double-list it as missing.
          if (s === 'options_flow' && optionsProviderNotConfigured) return false;
          return [
            'verified_current_quote',
            'options_flow',
            'vix_context',
            'economic_calendar',
            'calendar_provider_unavailable',
            'news_cache_empty',
          ].includes(s);
        })
        .map((s) =>
          ({
            verified_current_quote: 'verified current quote',
            options_flow: 'options flow',
            vix_context: 'VIX / volatility context',
            economic_calendar: 'economic calendar',
            calendar_provider_unavailable: 'calendar provider',
            news_cache_empty: 'news cache (empty this cycle)',
          } as Record<string, string>)[s] || s,
        );
      const parts: string[] = [];
      if (contextInjected) {
        let liveBit = 'Adversyn live context injected';
        if (newsCount !== null) {
          liveBit += ` (${newsCount} news items`;
          if (symbolCount !== null && symbolCount > 0) {
            liveBit += `; ${symbolCount} mentioning ${r?.symbol || 'symbol'}`;
          }
          liveBit += ')';
        }
        if (sources.length > 0) {
          liveBit += `. Sources: ${sources.join(', ')}.`;
        }
        parts.push(liveBit);
        // // [data-health-frontend-v1]
        // Surface registry-aware extras when present.
        const quoteStatus = String(dq?.quote_status || "").toUpperCase();
        const quoteProvider = String(dq?.quote_provider || "").toLowerCase();
        if (quoteStatus === "FALLBACK_ACTIVE" && quoteProvider) {
          parts.push(`Quote fallback: ${quoteProvider}.`);
        }
        if (optionsProviderNotConfigured && !isCrypto) {
          parts.push("Options provider not configured (decision confidence may be reduced).");
        }
        if (isCrypto && optionsStatus === "NOT_APPLICABLE") {
          parts.push("Options flow: not applicable for crypto.");
        }
        const researchChain: string[] = Array.isArray((dq as any)?.research_fallback_chain_configured)
          ? ((dq as any).research_fallback_chain_configured as string[])
          : Array.isArray((dq as any)?.research_fallback_chain)
            ? ((dq as any).research_fallback_chain as string[])
            : [];
        if (researchChain.length > 0) {
          parts.push(`Research fallback chain: ${researchChain.join(" \u2192 ")}.`);
        }

      } else {
        parts.push('Adversyn live context: UNAVAILABLE this cycle.');
      }
      if (namedMissing.length > 0) {
        parts.push(`Specific missing components: ${namedMissing.slice(0, 4).join(', ')}.`);
      }
      const combined = parts.join(' ');
      // Only push as an additional bullet if it isn't already implied by the
      // primary recommendation, to avoid duplicating text.
      if (
        combined &&
        !recommendations.some(
          (rec) =>
            rec.toLowerCase().includes('live context is available') ||
            rec.toLowerCase().includes('adversyn live context'),
        )
      ) {
        recommendations.push(combined);
      }
    }

    const summaryText =
      (typeof r?.synthesis?.summary === 'string' && r.synthesis.summary.trim())
        ? r.synthesis.summary.trim()
        : (typeof r?.message === 'string' && r.message.trim()
            ? r.message.trim()
            : 'No analysis available.');

    // 2026-05-19 two-phase: pluck the deliberation_request_id from the
    // backend response so the UI can poll /deliberate. Also surface the
    // initial deterministic council_deliberation payload as a seed.
    const cd = (r as any)?.council_deliberation || {};
    const deliberationRequestId: string | undefined =
      cd?.deliberation_request_id || cd?.request_id || r?.request_id;
    const seedDeliberation: CouncilDeliberation = {
      status: ((cd?.deliberation_status as string) || 'pending').toLowerCase() as CouncilDeliberation['status'],
      finalDecision: cd?.final_decision,
      confidence: typeof cd?.confidence === 'number' ? cd.confidence : undefined,
      timeHorizon: cd?.time_horizon,
      decisionReason: cd?.decision_reason,
      supportingEvidence: Array.isArray(cd?.supporting_evidence) ? cd.supporting_evidence : undefined,
      riskFlags: Array.isArray(cd?.risk_flags) ? cd.risk_flags : undefined,
      missingData: Array.isArray(cd?.missing_data) ? cd.missing_data : undefined,
      agreements: Array.isArray(cd?.agreements) ? cd.agreements : undefined,
      disagreements: Array.isArray(cd?.disagreements) ? cd.disagreements : undefined,
      whatWouldChangeDecision: Array.isArray(cd?.what_would_change_decision) ? cd.what_would_change_decision : undefined,
      researchContext: typeof cd?.research_context === 'string' ? cd.research_context : undefined,
      deliberationProvider: cd?.deliberation_provider,
      advisoryOnly: cd?.advisory_only === true,
      advisoryNote: typeof cd?.advisory_note === 'string' ? cd.advisory_note : undefined,
    };

    return {
      analysisId: r.request_id || `council-${Date.now()}`,
      summary: summaryText,
      insights,
      confidence,
      risks,
      recommendations,
      timestamp:
        r?.data_quality?.last_updated || new Date().toISOString(),
      deliberationRequestId,
      deliberation: seedDeliberation,
    };
  }

  // ── Legacy envelope fallback (unchanged behavior) ───────────────────
  const data: CouncilGatewayData = response?.data ?? {};

  const agents = data?.agents_consulted ?? [];
  const insights: CouncilInsight[] = agents.map((agent) => ({
    agent,
    content: `${agent} analysis contributed to the council decision.`,
  }));

  const analysisText = data?.analysis ?? '';
  const risks: CouncilRisk[] = [];
  if (analysisText.toLowerCase().includes('risk') || analysisText.toLowerCase().includes('caution')) {
    risks.push({ label: 'Analysis contains risk signals', severity: 'medium' });
  }
  if ((data?.confidence ?? 0) < 0.3) {
    risks.push({ label: 'Low confidence score', severity: 'high' });
  }

  const recommendations = (data?.recommendations ?? []).filter(
    (r) => typeof r === 'string' && r.length > 0,
  );

  return {
    analysisId: data?.request_id ?? `council-${Date.now()}`,
    summary: data?.analysis ?? 'No analysis available.',
    insights,
    confidence: data?.confidence ?? 0,
    risks,
    recommendations,
    timestamp: response?.meta?.timestamp ?? new Date().toISOString(),
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  ERROR CLASSIFICATION
// ═══════════════════════════════════════════════════════════════════════

export function classifyCouncilError(error: unknown): CouncilErrorType {
  if (error instanceof CouncilApiError) {
    if (error.code === 'VALIDATION_ERROR') return 'validation';
    if (error.statusCode >= 500) return 'server';
    if (error.statusCode === 408 || error.code === 'TIMEOUT') return 'timeout';
    return 'server';
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'timeout';
  }

  if (error instanceof TypeError && error.message.includes('fetch')) {
    return 'network';
  }

  return 'unknown';
}

// ═══════════════════════════════════════════════════════════════════════
//  RUN COUNCIL ANALYSIS (with retries)
// ═══════════════════════════════════════════════════════════════════════

export interface CouncilAdapterResult {
  success: boolean;
  result: NormalizedCouncilResult | null;
  error: string | null;
  errorType: CouncilErrorType | null;
  retriesUsed: number;
}

export async function runCouncilAnalysis(
  input: CouncilAnalysisRequest,
  onRetry?: (attempt: number) => void,
): Promise<CouncilAdapterResult> {
  let lastError: unknown = null;
  let retriesUsed = 0;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      if (attempt > 0) {
        retriesUsed = attempt;
        onRetry?.(attempt);
        await sleep(RETRY_DELAY_MS * attempt);
      }

      const response = await analyzeCouncil(input, { debug: debugMode });
      const normalized = normalizeCouncilResponse(response);

      return {
        success: true,
        result: normalized,
        error: null,
        errorType: null,
        retriesUsed,
      };
    } catch (err) {
      lastError = err;

      // Don't retry validation errors — they won't succeed on retry
      const errorType = classifyCouncilError(err);
      if (errorType === 'validation') {
        return {
          success: false,
          result: null,
          error: err instanceof Error ? err.message : 'Validation failed',
          errorType,
          retriesUsed,
        };
      }
    }
  }

  const errorType = classifyCouncilError(lastError);
  return {
    success: false,
    result: null,
    error: lastError instanceof Error ? lastError.message : 'Council analysis failed',
    errorType,
    retriesUsed,
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  TWO-PHASE DELIBERATION POLLING (2026-05-19)
// ═══════════════════════════════════════════════════════════════════════

/** Map the raw poll envelope into the camelCase frontend shape. */
export function mapDeliberationPollToCouncilDeliberation(
  poll: CouncilDeliberationPollResult,
): CouncilDeliberation {
  return {
    status: poll.status,
    finalDecision: poll.final_decision,
    confidence: typeof poll.confidence === 'number' ? poll.confidence : undefined,
    timeHorizon: poll.time_horizon,
    decisionReason: poll.decision_reason,
    supportingEvidence: poll.supporting_evidence,
    riskFlags: poll.risk_flags,
    missingData: poll.missing_data,
    agreements: poll.agreements,
    disagreements: poll.disagreements,
    whatWouldChangeDecision: poll.what_would_change_decision,
    researchContext: poll.research_context,
    deliberationProvider: poll.deliberation_provider,
    advisoryOnly: poll.advisory_only === true,
    advisoryNote: poll.advisory_note,
    deliberatorErrorCode: poll.deliberator_error_code,
    deliberatorErrorMessage: poll.deliberator_error_message,
  };
}

/**
 * Poll the /deliberate endpoint for an async deliberation result.
 * Calls `onUpdate` after each poll so the UI can re-render. Returns when
 * the status is completed/unavailable/unknown, or when the total budget
 * expires (callers should show "deliberation unavailable" if budget exceeded).
 *
 * Returns the final result. The caller is expected to render based on the
 * `status` field and final fields.
 */
export async function pollDeliberationUntilDone(
  requestId: string,
  options?: {
    intervalMs?: number;
    totalBudgetMs?: number;
    onUpdate?: (d: CouncilDeliberation) => void;
    abortSignal?: AbortSignal;
  },
): Promise<CouncilDeliberation> {
  const intervalMs = options?.intervalMs ?? 1500;
  const totalBudgetMs = options?.totalBudgetMs ?? 30000;
  const onUpdate = options?.onUpdate;
  const startedAt = Date.now();
  let lastDeliberation: CouncilDeliberation = { status: 'pending' };

  // First poll attempt immediately (don't wait the interval before the
  // first call — backend often has the deterministic seed available right
  // away).
  let firstAttempt = true;
  while (true) {
    if (options?.abortSignal?.aborted) {
      return { status: 'unknown', deliberatorErrorCode: 'ABORTED' };
    }
    if (!firstAttempt) {
      await sleep(intervalMs);
    }
    firstAttempt = false;

    const elapsedMs = Date.now() - startedAt;
    if (elapsedMs >= totalBudgetMs) {
      // Final budget exceeded — return the last status (still pending) so
      // the UI can render "LLM deliberation unavailable; showing initial
      // analysis" instead of looking like a hard failure.
      return {
        ...lastDeliberation,
        status: lastDeliberation.status === 'pending' ? 'unavailable' : lastDeliberation.status,
        deliberatorErrorCode: lastDeliberation.deliberatorErrorCode ?? 'POLL_TIMEOUT',
        deliberatorErrorMessage:
          lastDeliberation.deliberatorErrorMessage ??
          `Polling exceeded ${Math.round(totalBudgetMs / 1000)}s budget.`,
      };
    }

    let poll: CouncilDeliberationPollResult;
    try {
      poll = await pollCouncilDeliberation(requestId);
    } catch {
      // Network-level error in our wrapper would have returned 'unknown';
      // any throw here means an unexpected condition (e.g. validation).
      // Treat as transient — keep polling until budget runs out.
      continue;
    }

    lastDeliberation = mapDeliberationPollToCouncilDeliberation(poll);
    onUpdate?.(lastDeliberation);

    if (
      lastDeliberation.status === 'completed' ||
      lastDeliberation.status === 'unavailable' ||
      (lastDeliberation.status === 'unknown' && elapsedMs > 4000)
      // Unknown after 4s = no record (TTL expired or backend lost it) — stop.
    ) {
      return lastDeliberation;
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════
//  UTILS
// ═══════════════════════════════════════════════════════════════════════

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
